import * as React from "react";
import { useDocument } from "@/hooks/useDocument";
import { useUpdateDocument } from "@/hooks/useUpdateDocument";
import { useUpdateDocumentText } from "@/hooks/useUpdateDocumentText";
import { useUser } from "@/hooks/useUser";

export const useDocumentChangeListener = () => {
  const { user } = useUser();
  const { data: document } = useDocument();
  const updateDocumentMutation = useUpdateDocument();
  const updateDocumentTextMutation = useUpdateDocumentText();

  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Debug log for hook re-renders and dependency changes
  // console.log("useDocumentChangeListener hook rendered", ...);

  React.useEffect(() => {
    // Use a reference to store the cleanup function
    // We use a ref for the cleanup function because it's assigned asynchronously
    // and we need to access it in the cleanup return
    let removeListener: (() => Promise<void>) | undefined;

    const initDocumentListener = async () => {
      try {
        const { setupDocumentChangeListener } = await import("../utils/document.utils");
        const { getDocumentText } = await import("@/taskpane/taskpane");

        // The setup function now returns a removal function
        removeListener = await setupDocumentChangeListener(async () => {
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }

          debounceTimerRef.current = setTimeout(async () => {
            try {
              if (document?._id && document?.upload_text_url) {
                const currentContent = await getDocumentText();
                updateDocumentTextMutation.mutate({
                  url: document.upload_text_url,
                  text: currentContent,
                });
              } else if (document?._id) {
                const currentContent = await getDocumentText();
                updateDocumentMutation.mutate({
                  id: document._id,
                  content: currentContent,
                });
              }
            } catch (error) {
              console.error("Failed to update document content:", error);
            }
          }, 1500);
        });
      } catch (error) {
        console.error("Failed to setup document change listener:", error);
      }
    };

    if (user) {
      initDocumentListener();
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Execute the listener removal if it exists
      if (removeListener) {
        removeListener().catch((err) => console.error("Error removing listener:", err));
      }
    };
  }, [user, document?._id, document?.upload_text_url]); // Removed mutation objects from dependencies to prevent re-setup loops
};
